// Copyright 2020 Sorama B.V.

import { autoinject, computedFrom, observable } from "aurelia-framework";
import { MeasurementWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementModuleCatalog";
import { CanvasCollectionDTO, CanvasDTO, Dimension } from "datatypes/SoramaWebApi";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import {
    WorkflowPropertyBindingMode,
    WorkflowPropertyScope,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { ProjectCanvasCollectionRepository } from "repositories/ProjectCanvasCollectionRepository";
import { ProjectRepository } from "repositories/ProjectRepository";
import { IProjectManagementService } from "services/Project/IProjectManagementService";

@autoinject
export class PresentationPageModule implements IWorkflowModuleLifetime {
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, scope: WorkflowPropertyScope.workflow })
    public canvasId: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, scope: WorkflowPropertyScope.workflow })
    public canvasCollectionId: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public readOnly: boolean;

    @observable
    public selectedItem: CanvasCollectionDTO;

    protected canvasCollectionName: string;

    @computedFrom("canvasId")
    protected get activeCanvas(): CanvasDTO {
        if (this.canvasId == undefined || this.canvasCollectionId == undefined || this.canvasPages == undefined) {
            return undefined;
        }

        return this.canvasPages.find((x) => x.id == this.canvasId);
    }

    @computedFrom("activeCanvas", "canvasPages.length")
    protected get activeCanvasPageNumber(): number {
        if (this.activeCanvas == undefined) {
            return undefined;
        }

        return this.canvasPages.findIndex((x) => x.id == this.canvasId);
    }

    protected canvasPages: CanvasDTO[];

    constructor(
        private readonly shellviewManager: ShellViewManager,
        private readonly projectManagement: IProjectManagementService,
        private readonly projectRepository: ProjectRepository,
        private readonly projectCanvasCollectionRepository: ProjectCanvasCollectionRepository
    ) {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): void | Promise<void> {}

    public deactivateModule(): void | Promise<void> {}

    protected nextPage(): void {
        if (this.canvasPages == undefined) {
            return;
        }

        this.canvasId = this.canvasPages[Math.min(this.canvasPages.length - 1, this.activeCanvasPageNumber + 1)].id;
    }

    protected previousPage(): void {
        if (this.canvasPages == undefined) {
            return;
        }

        this.canvasId = this.canvasPages[Math.max(0, this.activeCanvasPageNumber - 1)].id;
    }

    protected addPage(): Promise<void> {
        if (this.canvasCollectionId == undefined) {
            return Promise.reject("No presentation selected");
        }

        return this.projectCanvasCollectionRepository
            .createPage(this.canvasCollectionId, { width: 4, height: 3 } as Dimension, this.canvasId)
            .then((x) => {
                this.canvasId = x.id;

                // Refresh the collection
                this.canvasCollectionIdChanged();
            });
    }

    protected canvasCollectionIdChanged(): void {
        if (this.canvasCollectionId == undefined) {
            this.canvasPages = undefined;
            this.canvasId = undefined;
            return;
        }

        this.projectCanvasCollectionRepository.get(this.canvasCollectionId).then((x) => {
            if (this.selectedItem == undefined || this.selectedItem.id != this.canvasCollectionId) {
                this.selectedItem = x;
            }

            this.canvasCollectionName = x.name;
        });

        this.projectCanvasCollectionRepository
            .getPages(this.canvasCollectionId)
            .readToEnd()
            .then((pages) => {
                this.canvasPages = pages;

                if (
                    this.canvasPages.length > 0 &&
                    (this.canvasId == undefined || pages.findIndex((x) => x.id == this.canvasId) == -1)
                ) {
                    // Either no page seleceted or page is not in collection, so select the first page
                    this.canvasId = this.canvasPages[0].id;
                } else if (this.canvasPages.length === 0) {
                    this.canvasId = undefined;
                }
            });
    }

    protected createCanvas(canvasName: string): Promise<void> {
        return this.projectRepository
            .createPresentation(this.projectManagement.currentProject.item.id, canvasName)
            .then((x) => {
                this.canvasCollectionId = x.id;

                return this.addPage();
            });
    }

    protected selectedItemChanged(): void {
        this.canvasCollectionId = this.selectedItem.id;
    }

    protected editProject(): void {
        this.shellviewManager.openRightSideBar(MeasurementWorkflowModuleCatalog.ProjectManagementModule);
    }
}
